import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.extend(timezone);

// Display windows are entered and shown in Bangkok time, sent as ISO UTC.
export const DISPLAY_WINDOW_TZ = 'Asia/Bangkok';
const WALL_FORMAT = 'YYYY-MM-DDTHH:mm:ss';

export type DisplayWindowStatus = 'not-started' | 'showing' | 'expired';

// ISO UTC -> Date whose local fields hold the Bangkok wall time (what react-datepicker displays)
export const isoToPickerDate = (iso?: string | null): Date | null => (iso ? new Date(dayjs(iso).tz(DISPLAY_WINDOW_TZ).format(WALL_FORMAT)) : null);

// Date picked as Bangkok wall time -> ISO UTC
export const pickerDateToIso = (date?: Date | null): string | null => (date ? dayjs.tz(dayjs(date).format(WALL_FORMAT), DISPLAY_WINDOW_TZ).toISOString() : null);

// same rule as suggestion-service: startedAt <= now && (endedAt null || endedAt > now)
export const getDisplayWindowStatus = (startedAt?: string | null, endedAt?: string | null, now: Date = new Date()): DisplayWindowStatus => {
  if (startedAt && dayjs(startedAt).isAfter(now)) return 'not-started';
  if (endedAt && !dayjs(endedAt).isAfter(now)) return 'expired';
  return 'showing';
};

// end must be after start (or after now when start is empty); past end after the start is allowed
export const isInvalidDisplayWindow = (startedAt?: string | null, endedAt?: string | null, now: Date = new Date()): boolean =>
  !!endedAt && !dayjs(endedAt).isAfter(startedAt ? dayjs(startedAt) : now);

export const PICKER_STEP_MINUTES = 5;

// Bounds the pickers enforce (Bangkok wall time): no past days (earlier times today are fine), start < end.
// startMin is inclusive (00:00 today), endMin is exclusive (start, or now when start is empty).
export const getPickerBounds = (startedAt?: string | null, endedAt?: string | null, now: Date = new Date()) => {
  const wallNow = isoToPickerDate(now.toISOString()) as Date;
  return {
    startMin: dayjs(wallNow).startOf('day').toDate(),
    startMax: isoToPickerDate(endedAt),
    endMin: (isoToPickerDate(startedAt) ?? wallNow) as Date,
  };
};

export type PreviewMode = { type: 'now' } | { type: 'at'; previewAt: Date | string } | { type: 'all' };

type WithWindow = { startedAt?: string | null; endedAt?: string | null };

// Preview time: now floored to 5 minutes, seconds 0 (ISO UTC)
export const getDefaultPreviewAt = (now: Date = new Date()): string => {
  const base = dayjs(now).startOf('minute');
  return base.subtract(base.minute() % 5, 'minute').toISOString();
};

// section is hidden by an "At time" preview when its window is not current at the Preview time
export const isHiddenInPreview = (item: WithWindow, mode: PreviewMode): boolean =>
  mode.type === 'at' && getDisplayWindowStatus(item.startedAt, item.endedAt, dayjs(mode.previewAt).toDate()) !== 'showing';

// sections to save as the throwaway feed-preview Feed (cache filters by now, so windows are applied here then cleared)
export const getPreviewSections = <T extends WithWindow>(sections: T[], mode: PreviewMode): T[] =>
  mode.type === 'now' ? sections : sections.filter((item) => !isHiddenInPreview(item, mode)).map((item) => ({ ...item, startedAt: null, endedAt: null }));
